import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getSyllabusProgress, markSyllabusProgress } from '../../services/api';
import { SyllabusItem, TeacherCurriculumSubject } from '../../types';

interface SyllabusChecklistProps {
  /** Curriculum subjects for this grade ([] when nothing is indexed).
   *  The Global (custom-syllabus) checklist feeds a single pseudo-subject with
   *  `subject: null` and `chapter_number: null` — every subject/chapter-number
   *  read in here must tolerate null. */
  subjects: TeacherCurriculumSubject[];
  grade: number;
  section?: string | null;
  token: string;
  /** When this changes to a chapter title, expand + scroll to that chapter. */
  focusChapterTitle?: string;
  /** When set, add this free-text string as a "your own topic" (from voice). */
  pendingCustom?: string;
  /** Called once a pendingCustom has been consumed, so the parent can clear it. */
  onCustomConsumed?: () => void;
  /** Fired after any successful tick/untick — lets the parent refresh progress bars. */
  onTicksChanged?: () => void;
}

/** Stable key for a done sub-topic. subject null → '' consistently. Uses the
 *  RAW sub_topic string (incl. any "N.N " prefix) — matches what we send/store. */
const itemKey = (subject: string | null | undefined, chapterTitle: string, subTopic: string): string =>
  `${subject ?? ''}|${chapterTitle}|${subTopic}`;

/** Strip a leading "N.N " (e.g. "1.2 ") for a cleaner checkbox label. The raw
 *  string is still what we send to the API — this is display-only. */
const displaySubTopic = (raw: string): string => raw.replace(/^\d+(?:\.\d+)*\s+/, '');

/** completed_at ISO → "Jul 8". */
const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/** A ticked custom (non-curriculum) sub-topic the teacher typed. */
interface CustomItem {
  sub_topic: string;
  completed_at: string | null;
}

/** A checkbox toggle the teacher has selected but NOT yet saved. Holds
 *  everything markSyllabusProgress needs so we can commit it on "Add".
 *  `done` is the desired state (true = tick, false = untick). */
interface StagedChange {
  subject: string | null;
  chapterTitle: string;
  chapterNumber: number | null;
  subTopic: string;
  done: boolean;
}

const SyllabusChecklist: React.FC<SyllabusChecklistProps> = ({
  subjects,
  grade,
  section,
  token,
  focusChapterTitle,
  pendingCustom,
  onCustomConsumed,
  onTicksChanged,
}) => {
  // Primary subjects first, substitutes last (mirrors the old NcertTopicPicker).
  const orderedSubjects = useMemo(() => {
    const primary = subjects.filter((s) => !s.is_substitute);
    const subs = subjects.filter((s) => s.is_substitute);
    return [...primary, ...subs];
  }, [subjects]);

  // Subject key for tab state / element keys. Global's `subject: null` → ''.
  const subjKey = (s: TeacherCurriculumSubject): string => s.subject ?? '';

  const [activeKey, setActiveKey] = useState<string>(
    orderedSubjects[0] ? orderedSubjects[0].subject ?? '' : ''
  );
  // doneMap: itemKey → completed_at ISO for every SAVED ticked curriculum sub-topic.
  const [doneMap, setDoneMap] = useState<Record<string, string>>({});
  // staged: itemKey → desired change the teacher selected but hasn't saved yet.
  // Committed to the server only when they press "Add selected topics".
  const [staged, setStaged] = useState<Record<string, StagedChange>>({});
  const [savingStaged, setSavingStaged] = useState(false);
  const [stagedError, setStagedError] = useState<string | null>(null);
  const [customs, setCustoms] = useState<CustomItem[]>([]);
  // Split-pane: the chapter shown in the detail panel (subjectKey|title).
  const [selectedChapterKey, setSelectedChapterKey] = useState<string | null>(null);
  // Per-row inline error, keyed by itemKey.
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [customText, setCustomText] = useState('');
  const [customError, setCustomError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const chapterRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Keep the active tab valid when the curriculum (re)loads.
  useEffect(() => {
    if (!orderedSubjects.some((s) => (s.subject ?? '') === activeKey)) {
      setActiveKey(orderedSubjects[0] ? orderedSubjects[0].subject ?? '' : '');
    }
  }, [orderedSubjects, activeKey]);

  // Fetch progress on mount / when the class changes; build doneMap + customs.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    getSyllabusProgress(token, grade, section)
      .then((res) => {
        if (cancelled) return;
        const items = res.items || [];
        const map: Record<string, string> = {};
        const custom: CustomItem[] = [];
        for (const it of items) {
          // "Your own topics" are exactly the subject-null, BLANK-chapter rows.
          // Everything else — NCERT ticks (subject set) and Global ticks
          // (subject null, chapter_title set) — goes into doneMap; keys that
          // this instance doesn't render are simply never looked up.
          const isOwnTopic = (it.subject ?? '') === '' && (it.chapter_title ?? '') === '';
          if (isOwnTopic) {
            custom.push({ sub_topic: it.sub_topic, completed_at: it.completed_at });
          } else {
            const key = itemKey(it.subject, it.chapter_title, it.sub_topic);
            map[key] = it.completed_at || new Date().toISOString();
          }
        }
        setDoneMap(map);
        setCustoms(custom);
        // Fresh server state — drop any staged-but-unsaved selections.
        setStaged({});
        setStagedError(null);
      })
      .catch((e: any) => {
        if (!cancelled) setLoadError(e?.message || 'Could not load your progress.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // subjects is derived from the same load as grade/section upstream; re-run
    // when the class or curriculum shape changes.
  }, [token, grade, section, subjects]);

  // Imperative focus: expand + scroll to a chapter by title (from VoiceCapture).
  useEffect(() => {
    if (!focusChapterTitle) return;
    const target = focusChapterTitle.trim().toLowerCase();
    for (const s of orderedSubjects) {
      const ch = s.chapters.find((c) => c.title.trim().toLowerCase() === target);
      if (ch) {
        const sk = s.subject ?? '';
        setActiveKey(sk);
        setSelectedChapterKey(`${sk}|${ch.title}`);
        // Let the tab switch + selection render before scrolling.
        window.setTimeout(() => {
          chapterRefs.current[`${sk}|${ch.title}`]?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }, 60);
        break;
      }
    }
  }, [focusChapterTitle, orderedSubjects]);

  // Keep a valid chapter selected for the active subject: default to the first
  // chapter (or the first with any coverage) whenever selection falls out of scope.
  useEffect(() => {
    const subj = orderedSubjects.find((s) => (s.subject ?? '') === activeKey);
    if (!subj || subj.chapters.length === 0) return;
    const valid = subj.chapters.some(
      (ch) => `${subj.subject ?? ''}|${ch.title}` === selectedChapterKey
    );
    if (valid) return;
    const withDone = subj.chapters.find((ch) =>
      (ch.sub_topics || []).some((st) => doneMap[itemKey(subj.subject, ch.title, st)] != null)
    );
    const first = withDone ?? subj.chapters[0];
    setSelectedChapterKey(`${subj.subject ?? ''}|${first.title}`);
  }, [activeKey, orderedSubjects, doneMap, selectedChapterKey]);

  const setRowError = useCallback((key: string, msg: string | null) => {
    setRowErrors((prev) => {
      const next = { ...prev };
      if (msg) next[key] = msg;
      else delete next[key];
      return next;
    });
  }, []);

  /** Effective checkbox state = staged desire if any, else the saved state. */
  const isChecked = useCallback(
    (key: string): boolean => (key in staged ? staged[key].done : doneMap[key] != null),
    [staged, doneMap]
  );

  // Clicking a checkbox no longer saves — it just STAGES the change. If the new
  // desired state matches what's already saved, the key drops out of `staged`
  // (nothing to save). Committed later via "Add selected topics".
  const toggleSubTopic = useCallback(
    (
      // Global pseudo-subject rows arrive with subject/chapter_number null —
      // sent through to the API as-is (subject null + a chapter title marks a
      // Global tick server-side).
      subject: string | null,
      chapterTitle: string,
      chapterNumber: number | null,
      subTopic: string
    ) => {
      const key = itemKey(subject, chapterTitle, subTopic);
      setRowError(key, null);
      setStaged((prev) => {
        const committed = doneMap[key] != null;
        const current = key in prev ? prev[key].done : committed;
        const nextDesired = !current;
        const next = { ...prev };
        if (nextDesired === committed) {
          delete next[key]; // back to saved state — nothing staged
        } else {
          next[key] = { subject, chapterTitle, chapterNumber, subTopic, done: nextDesired };
        }
        return next;
      });
    },
    [doneMap, setRowError]
  );

  /** Drop all staged selections without saving. */
  const clearStaged = useCallback(() => {
    setStaged({});
    setStagedError(null);
  }, []);

  // Commit every staged change to the server, then fold the successes into
  // doneMap. Failures stay staged so the teacher can retry.
  const saveStaged = useCallback(async () => {
    const entries = Object.entries(staged);
    if (entries.length === 0 || savingStaged) return;
    setSavingStaged(true);
    setStagedError(null);
    const succeeded: string[] = [];
    let failure: string | null = null;
    for (const [key, ch] of entries) {
      try {
        await markSyllabusProgress(token, {
          grade,
          section,
          subject: ch.subject,
          chapter_number: ch.chapterNumber,
          chapter_title: ch.chapterTitle,
          sub_topic: ch.subTopic, // RAW string (incl. any "N.N " prefix)
          done: ch.done,
        });
        succeeded.push(key);
      } catch (e: any) {
        failure = e?.message || 'Some topics could not be saved. Please try again.';
      }
    }
    if (succeeded.length) {
      setDoneMap((prev) => {
        const next = { ...prev };
        for (const key of succeeded) {
          const ch = staged[key];
          if (ch.done) next[key] = new Date().toISOString();
          else delete next[key];
        }
        return next;
      });
      setStaged((prev) => {
        const next = { ...prev };
        for (const key of succeeded) delete next[key];
        return next;
      });
      onTicksChanged?.();
    }
    setStagedError(failure);
    setSavingStaged(false);
  }, [staged, savingStaged, token, grade, section, onTicksChanged]);

  const addCustomText = useCallback(
    async (raw: string) => {
      const text = raw.trim();
      if (!text) return;
      setCustomError(null);
      // Case-insensitive dedupe against existing customs.
      if (customs.some((c) => c.sub_topic.trim().toLowerCase() === text.toLowerCase())) {
        return;
      }
      const optimistic: CustomItem = { sub_topic: text, completed_at: new Date().toISOString() };
      setCustoms((prev) => [...prev, optimistic]);
      try {
        await markSyllabusProgress(token, {
          grade,
          section,
          subject: null,
          chapter_number: null,
          chapter_title: '',
          sub_topic: text,
          done: true,
        });
        onTicksChanged?.();
      } catch (e: any) {
        setCustoms((prev) => prev.filter((c) => c.sub_topic !== text));
        setCustomError(e?.message || 'Could not add that topic.');
      }
    },
    [customs, token, grade, section]
  );

  const submitCustomInput = useCallback(() => {
    addCustomText(customText);
    setCustomText('');
  }, [addCustomText, customText]);

  // Consume a free-text topic pushed in from VoiceTopicCapture's no-match path.
  useEffect(() => {
    if (!pendingCustom) return;
    addCustomText(pendingCustom);
    onCustomConsumed?.();
    // addCustomText is stable enough for this one-shot; deliberately keyed on
    // the incoming value so each new pendingCustom is handled once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCustom]);

  const removeCustom = useCallback(
    async (subTopic: string) => {
      const prevCustoms = customs;
      setCustoms((prev) => prev.filter((c) => c.sub_topic !== subTopic));
      setCustomError(null);
      try {
        await markSyllabusProgress(token, {
          grade,
          section,
          subject: null,
          chapter_number: null,
          chapter_title: '',
          sub_topic: subTopic,
          done: false,
        });
        onTicksChanged?.();
      } catch (e: any) {
        setCustoms(prevCustoms);
        setCustomError(e?.message || 'Could not remove that topic.');
      }
    },
    [customs, token, grade, section]
  );

  const activeSubject = orderedSubjects.find((s) => (s.subject ?? '') === activeKey) ?? null;

  // The chapter list, grouped by MODULE. The STEM knowledge base arrives as ONE
  // pseudo-subject holding every document — 319 chapters across 22 modules after
  // VU's 2026-08-28 hand-overs — so a flat list meant scrolling past 78 App
  // Inventor worksheets to reach a paper-circuit one. Each chapter already
  // carries its module in `subject`; this only groups on it.
  //
  // PRESENTATION ONLY. The tick key stays itemKey(activeSubject.subject, ...) —
  // every STEM tick on record is stored with subject NULL, so moving the module
  // into that key would orphan the progress facilitators have already marked.
  // Numbering restarts per module, which is what the facilitator reads off.
  const chapterRows = React.useMemo(() => {
    const chapters = activeSubject?.chapters ?? [];
    const rows: Array<
      | { kind: 'module'; label: string; done: number; total: number }
      | { kind: 'chapter'; ch: (typeof chapters)[number]; index: number }
    > = [];
    // Keep the order the API sent (already grouped by module), so this never
    // reshuffles a subject that has no modules at all — NCERT included.
    const order: string[] = [];
    const byModule = new Map<string, typeof chapters>();
    chapters.forEach((ch) => {
      const label = ((ch as { subject?: string | null }).subject || '').trim();
      if (!byModule.has(label)) {
        order.push(label);
        byModule.set(label, []);
      }
      byModule.get(label)!.push(ch);
    });
    // One unlabelled group = nothing to group by (NCERT, or a teacher's own
    // syllabus). Leave it exactly as it was.
    if (order.length === 1 && order[0] === '') {
      chapters.forEach((ch, index) => rows.push({ kind: 'chapter', ch, index }));
      return rows;
    }
    order.forEach((label) => {
      const group = byModule.get(label)!;
      let done = 0;
      let total = 0;
      group.forEach((ch) => {
        const subTopics = ch.sub_topics || [];
        total += subTopics.length;
        done += subTopics.filter(
          (st) => isChecked(itemKey(activeSubject!.subject, ch.title, st))
        ).length;
      });
      if (label) rows.push({ kind: 'module', label, done, total });
      group.forEach((ch, index) => rows.push({ kind: 'chapter', ch, index }));
    });
    return rows;
  }, [activeSubject, isChecked]);


  const customSection = (
    <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <p className="mb-2 text-sm font-medium text-slate-700">Your own topics</p>
      {customs.length > 0 && (
        <ul className="mb-3 space-y-1.5">
          {customs.map((c) => (
            <li
              key={c.sub_topic}
              className="flex items-center justify-between gap-2 rounded-lg bg-white px-3 py-2 text-sm text-slate-700"
            >
              <span className="flex items-center gap-2">
                <span aria-hidden className="text-emerald-600">
                  ☑
                </span>
                <span>{c.sub_topic}</span>
                {c.completed_at && (
                  <span className="text-xs text-slate-400">✓ {fmtDate(c.completed_at)}</span>
                )}
              </span>
              <button
                type="button"
                onClick={() => removeCustom(c.sub_topic)}
                aria-label={`Remove ${c.sub_topic}`}
                className="flex-shrink-0 rounded-full px-2 py-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <label className="mb-1 block text-xs font-medium text-slate-500">+ Add your own topic</label>
      <div className="flex gap-2">
        <input
          type="text"
          value={customText}
          onChange={(e) => setCustomText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submitCustomInput();
            }
          }}
          placeholder="e.g. Map reading practice"
          className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-violet-400 focus:outline-none"
        />
        <button
          type="button"
          onClick={submitCustomInput}
          disabled={!customText.trim()}
          className="rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Add
        </button>
      </div>
      {customError && <p className="mt-2 text-sm text-red-600">{customError}</p>}
    </div>
  );

  // Empty curriculum: custom-only mode.
  if (orderedSubjects.length === 0) {
    return (
      <div>
        <p className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">
          No syllabus found for Grade {grade} yet — add the topics you're covering below.
        </p>
        {customSection}
      </div>
    );
  }

  return (
    <div>
      {orderedSubjects.length > 1 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {orderedSubjects.map((s) => (
            <button
              key={subjKey(s)}
              type="button"
              onClick={() => setActiveKey(subjKey(s))}
              className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                subjKey(s) === activeKey
                  ? 'border-violet-600 bg-violet-600 text-white'
                  : 'border-[#E9E6F4] bg-white text-slate-600 hover:border-violet-300 hover:bg-violet-50'
              }`}
            >
              {s.label}
              {s.is_substitute && (
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[10px] font-normal ${
                    subjKey(s) === activeKey ? 'bg-violet-500 text-violet-50' : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  sub
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {loadError && (
        <p className="mb-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-700">{loadError}</p>
      )}

      {loading ? (
        <p className="text-sm text-slate-400">Loading your progress…</p>
      ) : activeSubject ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,300px)_minmax(0,1fr)]">
          {/* Left: chapters list */}
          <div className="flex max-h-[560px] flex-col gap-1.5 overflow-y-auto rounded-2xl border border-[#E9E6F4] bg-white p-2">
            {activeSubject.chapters.length === 0 ? (
              <p className="p-3 text-sm text-slate-400">No chapters listed for this subject.</p>
            ) : (
              chapterRows.map((row) => {
                if (row.kind === 'module') {
                  return (
                    <div
                      key={`mod|${row.label}`}
                      className="sticky top-0 z-10 -mx-2 mb-0.5 mt-1 flex items-baseline justify-between gap-2 bg-white/95 px-4 py-1.5 backdrop-blur first:mt-0"
                    >
                      <span className="truncate text-[11px] font-bold uppercase tracking-wide text-violet-500">
                        {row.label}
                      </span>
                      <span className="flex-none text-[11px] font-semibold text-slate-400">
                        {row.done}/{row.total}
                      </span>
                    </div>
                  );
                }
                const { ch, index: i } = row;
                const chKey = `${activeSubject!.subject ?? ''}|${ch.title}`;
                const subTopics = ch.sub_topics || [];
                const done = subTopics.filter(
                  (st) => isChecked(itemKey(activeSubject!.subject, ch.title, st))
                ).length;
                const pct = subTopics.length ? Math.round((done / subTopics.length) * 100) : 0;
                const selected = selectedChapterKey === chKey;
                return (
                  <button
                    key={chKey}
                    type="button"
                    ref={(el) => {
                      chapterRefs.current[chKey] = el;
                    }}
                    onClick={() => setSelectedChapterKey(chKey)}
                    className={`rounded-xl px-3 py-2.5 text-left transition-colors ${
                      selected ? 'bg-violet-50 ring-1 ring-violet-200' : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="flex h-6 w-6 flex-none items-center justify-center rounded-lg bg-slate-100 text-[11px] font-bold text-slate-500">
                        {i + 1}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">
                        {ch.title}
                      </span>
                      <span className="flex-none text-xs font-semibold text-slate-500">
                        {done}/{subTopics.length || 0}
                      </span>
                    </div>
                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className={`h-full rounded-full ${pct >= 100 ? 'bg-emerald-500' : 'bg-violet-600'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Right: selected chapter detail */}
          <div className="rounded-2xl border border-[#E9E6F4] bg-white p-4">
            {(() => {
              const ch =
                activeSubject.chapters.find(
                  (c) => `${activeSubject!.subject ?? ''}|${c.title}` === selectedChapterKey
                ) ?? activeSubject.chapters[0];
              if (!ch) return <p className="text-sm text-slate-400">Pick a chapter on the left.</p>;
              const subTopics = ch.sub_topics || [];
              const done = subTopics.filter(
                (st) => isChecked(itemKey(activeSubject!.subject, ch.title, st))
              ).length;
              const pct = subTopics.length ? Math.round((done / subTopics.length) * 100) : 0;
              return (
                <>
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <h3 className="text-base font-bold text-slate-900">{ch.title}</h3>
                    <span className="flex-none text-sm font-semibold text-slate-500">
                      {done}/{subTopics.length} · {pct}%
                    </span>
                  </div>
                  <div className="mb-4 h-2 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className={`h-full rounded-full ${pct >= 100 ? 'bg-emerald-500' : 'bg-violet-600'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  {subTopics.length === 0 ? (
                    <p className="text-xs text-slate-400">No sub-topics listed for this chapter.</p>
                  ) : (
                    <ul className="flex flex-col gap-0.5">
                      {subTopics.map((st) => {
                        const key = itemKey(activeSubject!.subject, ch.title, st);
                        const isDone = isChecked(key);
                        const isStaged = key in staged;
                        const err = rowErrors[key];
                        return (
                          <li key={key}>
                            <button
                              type="button"
                              onClick={() =>
                                toggleSubTopic(activeSubject!.subject, ch.title, ch.chapter_number, st)
                              }
                              className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-slate-50"
                            >
                              <span
                                aria-hidden
                                className={`flex-shrink-0 text-lg leading-none ${
                                  isStaged ? 'text-violet-600' : isDone ? 'text-emerald-600' : 'text-slate-300'
                                }`}
                              >
                                {isDone ? '☑' : '☐'}
                              </span>
                              <span
                                className={`flex-1 text-sm ${
                                  isDone ? 'text-slate-500 line-through' : 'text-slate-700'
                                }`}
                              >
                                {displaySubTopic(st)}
                              </span>
                              {isStaged ? (
                                <span className="flex-shrink-0 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700">
                                  Not saved
                                </span>
                              ) : (
                                isDone && (
                                  <span className="flex-shrink-0 text-xs text-slate-400">
                                    ✓ {fmtDate(doneMap[key])}
                                  </span>
                                )
                              )}
                            </button>
                            {err && <p className="px-2 pb-1 text-xs text-red-600">{err}</p>}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      ) : null}

      {Object.keys(staged).length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-violet-200 bg-violet-50 p-3">
          <p className="text-sm font-medium text-violet-800">
            {Object.keys(staged).length} topic{Object.keys(staged).length > 1 ? 's' : ''} selected —
            not saved yet
          </p>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={clearStaged}
              disabled={savingStaged}
              className="rounded-lg px-3 py-2 text-sm font-medium text-slate-500 hover:text-slate-700 disabled:opacity-50"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={saveStaged}
              disabled={savingStaged}
              className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {savingStaged
                ? 'Adding…'
                : `Add ${Object.keys(staged).length} topic${Object.keys(staged).length > 1 ? 's' : ''}`}
            </button>
          </div>
          {stagedError && <p className="w-full text-sm text-red-600">{stagedError}</p>}
        </div>
      )}

      {customSection}
    </div>
  );
};

export default SyllabusChecklist;
